import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search,
  Filter,
  ExternalLink,
  X,
  Rss,
  Loader2,
  Sun,
  Moon,
  ArrowRight,
  Mail,
  Sparkles
} from "lucide-react";
import AskTheFeed from "./AskTheFeed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Theme Hook ──
const useTheme = () => {
  const [dark, setDark] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("bb-theme") === "dark";
    }
    return false;
  });

  useEffect(() => {
    localStorage.setItem("bb-theme", dark ? "dark" : "light");
  }, [dark]);

  return { dark, toggle: () => setDark(d => !d) };
};

// ── Theme classes helper ──
const t = (dark, light, darkCls) => (dark ? darkCls : light);

// ── Date formatting ──
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0 && diffHours < 24) {
    if (diffHours < 1) return "Just now";
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

// ── Logo ──
const Logo = ({ dark, large }) => (
  <div className="flex items-center gap-2.5">
    <svg viewBox="0 0 22 22" className={large ? "w-8 h-8" : "w-6 h-6"} fill="none">
      <rect x="1" y="1" width="9" height="9" rx="2.5" fill="#10b981"/>
      <rect x="12" y="1" width="9" height="9" rx="2.5" fill="#10b981" opacity="0.45"/>
      <rect x="1" y="12" width="9" height="9" rx="2.5" fill="#10b981" opacity="0.45"/>
      <rect x="12" y="12" width="9" height="9" rx="2.5" fill="#10b981" opacity="0.2"/>
    </svg>
    <span className={`${large ? "text-xl" : "text-base"} font-bold tracking-tight ${dark ? "text-gray-100" : "text-gray-900"}`}
      style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "-0.02em" }}>
      bitz<span className="text-emerald-500">&</span>blockz
    </span>
  </div>
);

// ── Badge Components ──
const ImportanceBadge = ({ importance, dark }) => {
  if (!importance) return <span className={dark ? "text-gray-600" : "text-gray-300"}>—</span>;
  const styles = {
    high: dark
      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"
      : "bg-emerald-50 text-emerald-700 border-emerald-200",
    medium: dark
      ? "bg-amber-500/15 text-amber-400 border-amber-500/25"
      : "bg-amber-50 text-amber-700 border-amber-200",
    low: dark
      ? "bg-gray-500/15 text-gray-400 border-gray-600"
      : "bg-gray-50 text-gray-500 border-gray-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase border rounded-full ${styles[importance] || styles.low}`}>
      {importance}
    </span>
  );
};

const StatusBadge = ({ status, dark }) => {
  if (!status) return <span className={dark ? "text-gray-600" : "text-gray-300"}>—</span>;
  const styles = {
    completed: dark
      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"
      : "bg-emerald-50 text-emerald-700 border-emerald-200",
    in_progress: dark
      ? "bg-amber-500/15 text-amber-400 border-amber-500/25"
      : "bg-amber-50 text-amber-700 border-amber-200",
    planned: dark
      ? "bg-violet-500/15 text-violet-400 border-violet-500/25"
      : "bg-violet-50 text-violet-700 border-violet-200",
    approved: dark
      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"
      : "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const label = status === "in_progress" ? "In Progress" : status;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium capitalize border rounded-full ${styles[status] || styles.completed}`}>
      {label}
    </span>
  );
};

const CategoryBadge = ({ category, dark }) => {
  if (!category) return <span className={dark ? "text-gray-600" : "text-gray-300"}>—</span>;
  const colorMap = {
    regulation_legal: dark ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-red-50 text-red-600 border-red-200",
    defi: dark ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-blue-50 text-blue-600 border-blue-200",
    security: dark ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-red-50 text-red-600 border-red-200",
    partnership: dark ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-emerald-50 text-emerald-600 border-emerald-200",
    infrastructure: dark ? "bg-violet-500/10 text-violet-400 border-violet-500/20" : "bg-violet-50 text-violet-600 border-violet-200",
    funding: dark ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-emerald-50 text-emerald-600 border-emerald-200",
    stablecoins_payments: dark ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-blue-50 text-blue-600 border-blue-200",
    token_events: dark ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-amber-50 text-amber-600 border-amber-200",
    corporate: dark ? "bg-gray-500/10 text-gray-400 border-gray-600" : "bg-gray-50 text-gray-600 border-gray-200",
    nft_gaming: dark ? "bg-violet-500/10 text-violet-400 border-violet-500/20" : "bg-violet-50 text-violet-600 border-violet-200",
    ai_crypto: dark ? "bg-violet-500/10 text-violet-400 border-violet-500/20" : "bg-violet-50 text-violet-600 border-violet-200",
    rwa: dark ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-blue-50 text-blue-600 border-blue-200",
    governance: dark ? "bg-gray-500/10 text-gray-400 border-gray-600" : "bg-gray-50 text-gray-600 border-gray-200",
    product_launch: dark ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-emerald-50 text-emerald-600 border-emerald-200",
    release: dark ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-blue-50 text-blue-600 border-blue-200",
    upgrade: dark ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-blue-50 text-blue-600 border-blue-200",
    personnel: dark ? "bg-gray-500/10 text-gray-400 border-gray-600" : "bg-gray-50 text-gray-600 border-gray-200",
    network_incident: dark ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-red-50 text-red-600 border-red-200",
  };
  const fallback = dark ? "bg-gray-500/10 text-gray-400 border-gray-600" : "bg-gray-50 text-gray-500 border-gray-200";
  const label = category.replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium capitalize border rounded-full whitespace-nowrap ${colorMap[category] || fallback}`}>
      {label}
    </span>
  );
};

const AssetTicker = ({ assets, dark }) => {
  if (!assets) return <span className={dark ? "text-gray-600" : "text-gray-300"}>—</span>;
  const list = assets.split(",").map(a => a.trim()).filter(Boolean);
  if (!list.length) return <span className={dark ? "text-gray-600" : "text-gray-300"}>—</span>;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {list.slice(0, 3).map((asset, i) => (
        <span
          key={i}
          className={`px-1.5 py-0.5 text-[10px] font-mono rounded-md border ${
            dark
              ? "bg-gray-800 text-emerald-400 border-gray-700"
              : "bg-gray-50 text-emerald-700 border-gray-200"
          }`}
        >
          {asset}
        </span>
      ))}
      {list.length > 3 && (
        <span className={`text-[10px] ${dark ? "text-gray-500" : "text-gray-400"}`}>+{list.length - 3}</span>
      )}
    </div>
  );
};

// ── Mobile Card ──
const MobileCard = ({ item, dark }) => (
  <div
    className={`p-4 border-b last:border-b-0 transition-colors ${
      dark ? "border-gray-800 hover:bg-gray-800/40" : "border-gray-100 hover:bg-gray-50"
    }`}
    data-testid={`feed-item-${item.id}`}
  >
    <div className="flex items-start justify-between gap-2 mb-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <CategoryBadge category={item.category} dark={dark} />
        <ImportanceBadge importance={item.importance} dark={dark} />
      </div>
      <span className={`text-[11px] font-mono whitespace-nowrap flex-shrink-0 ${dark ? "text-gray-500" : "text-gray-400"}`}>
        {formatDate(item.event_date || item.source_timestamp || item.created_at)}
      </span>
    </div>
    <p className={`text-sm leading-relaxed ${dark ? "text-gray-200" : "text-gray-800"}`}>
      {item.summary || item.raw_text}
    </p>
    <div className="flex items-center justify-between mt-2">
      {item.source_author && (
        <span className={`text-[11px] font-mono ${dark ? "text-gray-500" : "text-gray-400"}`}>
          @{item.source_author}
        </span>
      )}
      {item.assets && <AssetTicker assets={item.assets} dark={dark} />}
      {item.source_url && (
        <a
          href={item.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`ml-auto ${dark ? "text-gray-600 hover:text-blue-400" : "text-gray-400 hover:text-blue-500"}`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  </div>
);

// ── Table Row ──
const TableRow = ({ item, dark }) => (
  <tr
    className={`border-b transition-colors group ${
      dark
        ? "border-gray-800 hover:bg-gray-800/50"
        : "border-gray-100 hover:bg-gray-50/70"
    }`}
    data-testid={`feed-item-${item.id}`}
  >
    <td className={`py-3.5 px-4 text-sm ${dark ? "text-gray-200" : "text-gray-800"}`}>
      <div className="flex items-start gap-2">
        <span className="leading-relaxed">{item.summary || item.raw_text}</span>
        {item.source_url && (
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${
              dark ? "text-gray-500 hover:text-blue-400" : "text-gray-400 hover:text-blue-500"
            }`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
      {item.source_author && (
        <span className={`text-[11px] font-mono mt-1 block ${dark ? "text-gray-500" : "text-gray-400"}`}>
          @{item.source_author}
        </span>
      )}
    </td>
    <td className="py-3.5 px-3"><AssetTicker assets={item.assets} dark={dark} /></td>
    <td className="py-3.5 px-3"><CategoryBadge category={item.category} dark={dark} /></td>
    <td className="py-3.5 px-3"><ImportanceBadge importance={item.importance} dark={dark} /></td>
    <td className="py-3.5 px-3"><StatusBadge status={item.status} dark={dark} /></td>
    <td className={`py-3.5 px-3 font-mono text-[11px] whitespace-nowrap ${dark ? "text-gray-500" : "text-gray-400"}`}>
      {formatDate(item.event_date || item.source_timestamp || item.created_at)}
    </td>
  </tr>
);

// ── Filter Dropdown (Label + Bold Value pattern) ──
const LabeledFilter = ({ label, value, onChange, options, testId, dark }) => {
  const hasValue = value && value !== "all";
  const displayValue = hasValue
    ? (options.find(o => o.value === value)?.label || value)
    : "All";
  
  return (
    <Select value={value || "all"} onValueChange={onChange}>
      <SelectTrigger
        className={`h-9 px-3 text-xs rounded-lg border ${
          dark
            ? "bg-gray-800 border-gray-700 hover:bg-gray-750"
            : "bg-white border-gray-200 hover:bg-gray-50"
        }`}
        data-testid={testId}
      >
        <span>
          <span className={dark ? "text-gray-500" : "text-gray-500"}>{label} </span>
          <span className={`font-semibold ${
            hasValue
              ? (dark ? "text-emerald-400" : "text-emerald-600")
              : (dark ? "text-gray-200" : "text-gray-700")
          }`}>{displayValue}</span>
        </span>
      </SelectTrigger>
      <SelectContent className={`rounded-lg ${dark ? "bg-gray-800 border-gray-700 text-gray-200" : "bg-white border-gray-200 text-gray-900"}`}>
        <SelectItem
          value="all"
          className={dark ? "text-gray-200 focus:bg-gray-700 focus:text-white" : "text-gray-800 focus:bg-gray-100 focus:text-gray-900"}
        >All</SelectItem>
        {options.map(opt => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            className={dark ? "text-gray-200 focus:bg-gray-700 focus:text-white" : "text-gray-800 focus:bg-gray-100 focus:text-gray-900"}
          >{opt.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// ══════════════════════════════════════════
// ── MAIN PAGE ──
// ══════════════════════════════════════════
const PublicFeedPage = () => {
  const { dark, toggle } = useTheme();
  const [view, setView] = useState("table"); // "table" | "ask"
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [taxonomy, setTaxonomy] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [email, setEmail] = useState("");
  const offset = useRef(0);

  const [filters, setFilters] = useState(() => {
    const params = {};
    ["category", "importance", "assets", "status", "search"].forEach(key => {
      const value = searchParams.get(key);
      if (value) params[key] = value;
    });
    return params;
  });

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all") params.set(key, value);
    });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const fetchTaxonomy = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/taxonomy`);
      setTaxonomy(response.data);
    } catch (error) {
      console.error("Failed to fetch taxonomy:", error);
    }
  };

  const fetchItems = useCallback(async (reset = true) => {
    if (reset) { setLoading(true); offset.current = 0; } else { setLoadingMore(true); }
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "all") {
          params.set(key, value);
        }
      });
      params.set("offset", reset ? "0" : offset.current.toString());
      params.set("limit", "30");
      const response = await axios.get(`${BACKEND_URL}/api/public/feed?${params.toString()}`);
      if (reset) setItems(response.data.items);
      else setItems(prev => [...prev, ...response.data.items]);
      setTotal(response.data.total);
      offset.current = reset ? 30 : offset.current + 30;
    } catch (error) {
      console.error("Failed to fetch feed:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters]);

  useEffect(() => { fetchTaxonomy(); }, []);
  useEffect(() => { fetchItems(true); }, [fetchItems]);

  const clearFilters = () => setFilters({});
  const hasActiveFilters = Object.values(filters).some(v => v && v !== "all");

  // ── Theme-aware classes ──
  const bg = dark ? "bg-[#0c0c14]" : "bg-white";
  const bgSecondary = dark ? "bg-[#12121a]" : "bg-gray-50";
  const textPrimary = dark ? "text-gray-100" : "text-gray-900";
  const textSecondary = dark ? "text-gray-400" : "text-gray-500";
  const textMuted = dark ? "text-gray-500" : "text-gray-400";
  const border = dark ? "border-gray-800" : "border-gray-200";
  const borderLight = dark ? "border-gray-800/50" : "border-gray-100";
  const inputBg = dark ? "bg-gray-800 border-gray-700 text-gray-300" : "bg-white border-gray-200 text-gray-700";
  const hoverBg = dark ? "hover:bg-gray-800" : "hover:bg-gray-50";

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`} data-testid="public-feed">

      {/* ── Header ── */}
      <header className={`border-b ${border} sticky top-0 z-50 ${bg} transition-colors duration-300`}>
        <div className="w-full px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Logo dark={dark} large />
          </div>
          <div className="flex items-center gap-3">
            {/* View toggle */}
            <div className={`flex items-center rounded-lg border p-0.5 gap-0.5 ${dark ? "border-gray-800 bg-gray-900" : "border-gray-200 bg-gray-100"}`}>
              <button
                onClick={() => setView("table")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                  view === "table"
                    ? dark
                      ? "bg-[#1a1a26] text-[#e0e0e8] shadow-sm"
                      : "bg-white text-gray-900 shadow-sm"
                    : dark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                }`}
                data-testid="view-table"
              >
                Feed
              </button>
              <button
                onClick={() => setView("ask")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                  view === "ask"
                    ? dark
                      ? "bg-emerald-500/15 text-emerald-400 shadow-sm"
                      : "bg-emerald-50 text-emerald-700 shadow-sm"
                    : dark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                }`}
                data-testid="view-ask"
              >
                <Sparkles className="w-3 h-3" />
                Ask
              </button>
            </div>

            {/* RSS + theme */}
            <a
              href={`${BACKEND_URL}/api/public/rss`}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-2 rounded-lg transition-colors ${textMuted} ${hoverBg}`}
              title="RSS Feed"
              data-testid="rss-link"
            >
              <Rss className="w-4 h-4" />
            </a>
            <button
              onClick={toggle}
              className={`p-2 rounded-lg transition-colors ${textMuted} ${hoverBg}`}
              data-testid="theme-toggle"
              title={dark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero Tagline ── */}
      <div className={`${bg} transition-colors duration-300`}>
        <div className="w-full px-6 lg:px-10 pt-10 pb-6">
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight ${textPrimary}`}
            style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "-0.03em" }}>
            Crypto <span className="text-emerald-500">Intelligence</span>
          </h1>
          <p className={`mt-3 text-base max-w-xl ${textSecondary}`}>
            AI-curated blockchain events, governance, and market signals. Cut through the noise.
          </p>
        </div>
      </div>

      {/* ── Ask the Feed view ── */}
      {view === "ask" && (
        <div className={`transition-colors duration-300 ${bg}`}>
          <AskTheFeed dark={dark} />
        </div>
      )}

      {/* ── Filters ── */}
      {view === "table" && <div className={`border-b ${border} sticky top-[73px] z-40 ${bg} transition-colors duration-300`}>
        <div className="w-full px-6 lg:px-10 py-3">
          {/* Desktop */}
          <div className="hidden md:flex items-center gap-2.5">
            <div className="relative flex-1 min-w-[200px]">
              <Search className={`w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 ${textMuted}`} />
              <input
                value={filters.search || ""}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search events..."
                className={`h-9 w-full pl-9 pr-3 text-xs rounded-lg border outline-none transition-colors ${inputBg} focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/40`}
                data-testid="filter-search"
              />
            </div>
            <LabeledFilter
              label="Asset"
              value={filters.assets}
              onChange={(v) => setFilters({ ...filters, assets: v })}
              options={taxonomy.asset || []}
              testId="filter-assets"
              dark={dark}
            />
            <LabeledFilter
              label="Category"
              value={filters.category}
              onChange={(v) => setFilters({ ...filters, category: v })}
              options={taxonomy.category || []}
              testId="filter-category"
              dark={dark}
            />
            <LabeledFilter
              label="Importance"
              value={filters.importance}
              onChange={(v) => setFilters({ ...filters, importance: v })}
              options={taxonomy.importance || []}
              testId="filter-importance"
              dark={dark}
            />
            <LabeledFilter
              label="Status"
              value={filters.status}
              onChange={(v) => setFilters({ ...filters, status: v })}
              options={taxonomy.status || []}
              testId="filter-status"
              dark={dark}
            />
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className={`h-9 px-3 text-xs rounded-lg transition-colors ${textMuted} ${hoverBg}`}
                data-testid="clear-filters"
              >
                <X className="w-3.5 h-3.5 inline mr-1" />
                Clear
              </button>
            )}
            <div className={`ml-auto text-xs font-mono ${textMuted}`} data-testid="results-count">
              {total.toLocaleString()} events
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden flex items-center justify-between">
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild>
                <button
                  className={`h-9 px-4 text-xs rounded-lg border flex items-center gap-1.5 transition-colors ${
                    dark ? "border-gray-700 text-gray-400" : "border-gray-200 text-gray-500"
                  } ${hoverBg}`}
                  data-testid="mobile-filters-btn"
                >
                  <Filter className="w-3.5 h-3.5" />
                  Filters
                  {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                </button>
              </SheetTrigger>
              <SheetContent side="left" className={`w-80 p-0 ${dark ? "bg-[#0c0c14] border-gray-800" : "bg-white border-gray-200"}`}>
                <SheetHeader className={`p-4 border-b ${border}`}>
                  <SheetTitle className={textPrimary}>Filters</SheetTitle>
                </SheetHeader>
                <div className="p-4 space-y-4">
                  <div>
                    <label className={`text-[10px] uppercase font-medium mb-1.5 block ${textMuted}`}>Search</label>
                    <input value={filters.search || ""} onChange={(e) => setFilters({...filters, search: e.target.value})} placeholder="Search events..." className={`h-9 w-full px-3 text-sm rounded-lg border outline-none ${inputBg}`} />
                  </div>
                  <div>
                    <label className={`text-[10px] uppercase font-medium mb-1.5 block ${textMuted}`}>Asset</label>
                    <LabeledFilter label="Asset" value={filters.assets} onChange={(v) => setFilters({...filters, assets: v})} options={taxonomy.asset || []} testId="m-asset" dark={dark} />
                  </div>
                  <div>
                    <label className={`text-[10px] uppercase font-medium mb-1.5 block ${textMuted}`}>Category</label>
                    <LabeledFilter label="Category" value={filters.category} onChange={(v) => setFilters({...filters, category: v})} options={taxonomy.category || []} testId="m-cat" dark={dark} />
                  </div>
                  <div>
                    <label className={`text-[10px] uppercase font-medium mb-1.5 block ${textMuted}`}>Importance</label>
                    <LabeledFilter label="Importance" value={filters.importance} onChange={(v) => setFilters({...filters, importance: v})} options={taxonomy.importance || []} testId="m-imp" dark={dark} />
                  </div>
                  <div>
                    <label className={`text-[10px] uppercase font-medium mb-1.5 block ${textMuted}`}>Status</label>
                    <LabeledFilter label="Status" value={filters.status} onChange={(v) => setFilters({...filters, status: v})} options={taxonomy.status || []} testId="m-status" dark={dark} />
                  </div>
                  <button onClick={() => { clearFilters(); setMobileFiltersOpen(false); }} className={`w-full h-9 text-xs rounded-lg transition-colors ${textMuted} ${hoverBg}`}>
                    <X className="w-3.5 h-3.5 inline mr-1" />Clear all filters
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            <span className={`text-xs font-mono ${textMuted}`}>{total.toLocaleString()} events</span>
          </div>
        </div>
      </div>}

      {/* ── Table ── */}
      {view === "table" && <main className="w-full px-6 lg:px-10 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className={`w-5 h-5 animate-spin ${textMuted}`} />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Search className={`w-7 h-7 mb-3 ${textMuted}`} />
            <p className={textSecondary}>No events found</p>
            <p className={`text-sm mt-1 ${textMuted}`}>Check back later for new intel</p>
          </div>
        ) : (
          <div className={`rounded-xl border ${border} overflow-hidden`}>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left" data-testid="feed-table">
                <thead>
                  <tr className={`border-b ${border} text-[10px] font-mono uppercase ${textMuted} ${bgSecondary}`}>
                    <th className="py-3 px-4 font-medium">Event</th>
                    <th className="py-3 px-3 font-medium">Assets</th>
                    <th className="py-3 px-3 font-medium">Category</th>
                    <th className="py-3 px-3 font-medium">Importance</th>
                    <th className="py-3 px-3 font-medium">Status</th>
                    <th className="py-3 px-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <TableRow key={item.id} item={item} dark={dark} />
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden">
              {items.map(item => (
                <MobileCard key={item.id} item={item} dark={dark} />
              ))}
            </div>

            {items.length < total && (
              <div className={`py-5 text-center border-t ${border}`}>
                <button
                  onClick={() => fetchItems(false)}
                  disabled={loadingMore}
                  className={`h-10 px-6 text-sm rounded-full border transition-colors ${
                    dark
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                  data-testid="load-more"
                >
                  {loadingMore ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" />Loading...</>
                  ) : (
                    <>Load more <span className={textMuted}>({total - items.length} remaining)</span></>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </main>}

      {/* ── Newsletter Section ── */}
      <section className={`mt-16 border-t ${border}`}>
        <div className={`w-full px-6 lg:px-10 py-16`}>
          <div className={`max-w-lg mx-auto text-center`}>
            <div className={`w-10 h-10 rounded-xl ${dark ? "bg-emerald-500/10" : "bg-emerald-50"} flex items-center justify-center mx-auto mb-4`}>
              <Mail className={`w-5 h-5 ${dark ? "text-emerald-400" : "text-emerald-600"}`} />
            </div>
            <h2 className={`text-xl font-semibold mb-2 ${textPrimary}`}>
              Stay in the loop
            </h2>
            <p className={`text-sm mb-6 ${textSecondary}`}>
              Get the most important crypto news delivered to your inbox. No spam, just signal.
            </p>
            <div className="flex gap-2 max-w-sm mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={`flex-1 h-10 px-4 text-sm rounded-full border outline-none transition-colors ${inputBg} focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/40`}
                data-testid="newsletter-email"
              />
              <button
                className="h-10 px-5 text-sm font-medium rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors flex items-center gap-1.5"
                data-testid="newsletter-submit"
                onClick={() => { if (email) { setEmail(""); alert("Thanks for subscribing!"); } }}
              >
                Subscribe
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── About Section ── */}
      <section className={`border-t ${border} ${bgSecondary} transition-colors duration-300`}>
        <div className="w-full px-6 lg:px-10 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <Logo dark={dark} />
            <p className={`text-sm leading-relaxed mt-4 ${textSecondary}`}>
              bitz&blockz is a curated crypto news intelligence platform. 
              We aggregate, analyze, and categorize the most important events 
              across the blockchain ecosystem — powered by AI, refined by editors. 
              Our goal is to cut through the noise and deliver the signal that matters.
            </p>
            <div className={`flex items-center justify-center gap-6 mt-6 text-xs ${textMuted}`}>
              <a
                href={`${BACKEND_URL}/api/public/rss`}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-1.5 transition-colors ${dark ? "hover:text-amber-400" : "hover:text-amber-600"}`}
              >
                <Rss className="w-3.5 h-3.5" /> RSS Feed
              </a>
              <a href="/admin/login" className={`transition-colors ${dark ? "hover:text-gray-300" : "hover:text-gray-700"}`}>
                Admin
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={`border-t ${border} ${bg} transition-colors duration-300`}>
        <div className={`w-full px-6 lg:px-10 py-4 text-center text-[11px] ${textMuted}`}>
          &copy; {new Date().getFullYear()} bitz&blockz &middot; Crypto News Intelligence
        </div>
      </footer>
    </div>
  );
};

export default PublicFeedPage;
